import { useEffect, useRef, useState } from "react";
import { Bug } from "lucide-react";
import { useAuthStore } from "@/stores/auth";
import { APP_VERSION } from "@/lib/constants";

const STORAGE_KEY = "trace-debug-vv";

/** True when running as an installed (home-screen) PWA. */
function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * Live viewport diagnostics for the iOS-standalone-PWA drag/white-band hunt.
 *
 * Superadmin-only: a small bug button (bottom-left) toggles a fixed overlay
 * that samples the layout vs visual viewport, scroll/lock state, safe-area
 * insets, the open drawer's geometry and gesture/touch activity ~3×/second.
 * When the chat "floats" under a finger, exactly one of these numbers moves —
 * that number names the broken layer:
 *   - vv scale/offset changes → visual-viewport zoom/pan (zoom lock failing)
 *   - scroll/bodyPos changes  → document scroll (body lock failing)
 *   - nothing changes         → an element transform/rubber-band elsewhere.
 */
export function DebugViewportOverlay() {
  const user = useAuthStore((s) => s.user);
  const [enabled, setEnabled] = useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(STORAGE_KEY) === "on",
  );
  const [lines, setLines] = useState<string[]>([]);
  const gestures = useRef({ count: 0, prevented: "—" });
  const touchMoves = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    // Bubble-phase window listeners run AFTER lockPwaZoom's document handlers,
    // so defaultPrevented tells us whether the zoom lock actually fired.
    const onGesture = (e: Event) => {
      gestures.current.count += 1;
      gestures.current.prevented = e.defaultPrevented ? "prevented" : "NOT prevented";
    };
    const onTouchMove = () => {
      touchMoves.current += 1;
    };
    window.addEventListener("gesturestart", onGesture, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });

    // env() probe — computed padding exposes the real safe-area insets.
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;" +
      "padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom);";
    document.body.appendChild(probe);

    const update = () => {
      const vv = window.visualViewport;
      const de = document.documentElement;
      const probeStyle = getComputedStyle(probe);
      const surface = document.querySelector(
        ".assistant-page-ios, .drawer--mobile-fs.drawer--open, .drawer--workspace.drawer--open",
      );
      const surfaceRect = surface?.getBoundingClientRect();
      const footerRect = surface
        ?.querySelector(".assistant-page-ios__footer, .drawer__footer")
        ?.getBoundingClientRect();
      setLines(
        [
          `v${APP_VERSION} · ${isStandalone() ? "STANDALONE" : "browser"}`,
          `inner ${window.innerWidth}×${window.innerHeight} · clientH ${de.clientHeight} · screen ${screen.width}×${screen.height}`,
          vv
            ? `vv ${Math.round(vv.width)}×${Math.round(vv.height)} · scale ${vv.scale.toFixed(2)} · off ${Math.round(vv.offsetLeft)},${Math.round(vv.offsetTop)}`
            : "vv n/a",
          `scroll ${Math.round(window.scrollX)},${Math.round(window.scrollY)} · bodyPos ${document.body.style.position || "static"} ${document.body.style.top || ""} · lock ${document.body.dataset.traceScrollLock || "none"} · assistant ${document.body.dataset.traceAssistantMode || "drawer"}`,
          `safe top ${probeStyle.paddingTop} · bottom ${probeStyle.paddingBottom}`,
          `overX html ${de.scrollWidth - de.clientWidth} body ${document.body.scrollWidth - de.clientWidth}`,
          surfaceRect
            ? `surface ${Math.round(surfaceRect.top)}→${Math.round(surfaceRect.bottom)} (inner ${window.innerHeight} · gap ${Math.round(window.innerHeight - surfaceRect.bottom)})`
            : "drawer: closed",
          footerRect
            ? `footer bottom ${Math.round(footerRect.bottom)} · gap ${Math.round(window.innerHeight - footerRect.bottom)}`
            : "",
          `gestures ${gestures.current.count} (${gestures.current.prevented}) · touchmoves ${touchMoves.current}`,
        ].filter(Boolean),
      );
    };

    update();
    const interval = window.setInterval(update, 350);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.clearInterval(interval);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("gesturestart", onGesture);
      window.removeEventListener("touchmove", onTouchMove);
      probe.remove();
    };
  }, [enabled]);

  if (!user?.is_superadmin) return null;

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
    } catch {
      // In-memory toggle still works without storage.
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label={enabled ? "Hide viewport debug" : "Show viewport debug"}
        style={{
          position: "fixed",
          left: 8,
          bottom: `calc(96px + env(safe-area-inset-bottom, 0px))`,
          zIndex: 300,
          width: 34,
          height: 34,
          borderRadius: 17,
          display: "grid",
          placeItems: "center",
          background: enabled ? "rgba(220,60,60,0.9)" : "rgba(0,0,0,0.45)",
          color: "#fff",
          border: "none",
        }}
      >
        <Bug size={16} aria-hidden="true" />
      </button>
      {enabled && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            top: `calc(4px + env(safe-area-inset-top, 0px))`,
            left: 4,
            right: 4,
            zIndex: 300,
            pointerEvents: "none",
            background: "rgba(0,0,0,0.78)",
            color: "#7CFC9A",
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 10.5,
            lineHeight: 1.45,
            padding: "6px 8px",
            borderRadius: 8,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {lines.join("\n")}
        </div>
      )}
    </>
  );
}
